import {
  isBefore as isDateBefore,
  isEqual as isDateEqual,
  isValid as isValidDate,
  startOfToday,
} from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IModalRefProps } from 'ui/components/Modals/Modal/types';
import { EEventApplicationType, LocationOptionsEnum } from 'ui/enums';
import { ILocation } from 'ui/types/interfaces';

import { BlockedDate } from '~/components/CalendarInline/types';
import { queryClient } from '~/config/react-query.config';
import { PAGES } from '~/constants/pages.constants';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useChangeEcosystem } from '~/hooks/useChangeEcosystem';
import { useRouter } from '~/hooks/useRouter';
import {
  ActivityViewFilters,
  useActivityViewStore,
} from '~/pages/Authenticated/v2/ActivityView/store';
import { useTeamActivityViewStore } from '~/pages/Authenticated/v2/ActivityView/store/teams';
import useTeamApplicationController from '~/pages/Authenticated/v2/ActivityView/views/ActivityApplication/components/EventApplicationPanel/team-application-controller';
import { ApplicationActionStyleMapByType } from '~/pages/Authenticated/v2/ActivityView/views/ActivityApplication/constants/application-style-map';
import { useApplicationAvailabilityController } from '~/pages/Authenticated/v2/ActivityView/views/ActivityApplication/controllers/application-availability-controller';
import { useActivityApplicationController } from '~/pages/Authenticated/v2/ActivityView/views/ActivityApplication/controllers/application-controller';
import TeamService from '~/services/resources/teams';
import { ExecutionPlatform } from '~/store/slices/inApp/types';
import { redirectSliceActions } from '~/store/slices/redirect';
import { getActivitySpotsAvailable } from '~/utils/activity/activity-getters';
import { getLocationFromAddress, isLocationEqual } from '~/utils/getLocation';

const today = startOfToday();
export const isDateBlocked = (dueDate?: Date) =>
  dueDate && isValidDate(dueDate) && isDateBefore(dueDate, today);

export const useTeamInviteApplicationPanelController = () => {
  // -- Providers
  const { goToRoute } = useRouter();
  const { platform } = useAppSelector((state) => state.inApp);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const filters = useActivityViewStore(
    (state) => state.filters,
  ) as ActivityViewFilters;

  const setSelectedActivity = useActivityViewStore(
    (state) => state.setSelectedActivity,
  );

  const { teamInvite } = useTeamActivityViewStore((state) => state);

  const { setFilters } = useActivityViewStore((state) => state);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalRef = useRef<IModalRefProps>(null);

  const {
    activityDefinition,
    activities,
    activityType,
    selectedActivity,
    title,
    headerStyle,
  } = useActivityApplicationController();

  const teamController = useTeamApplicationController();

  const { applicationAvailability } = useApplicationAvailabilityController({
    activityDefinition,
    activity: selectedActivity,
    selectedTeamId: teamController.selectedTeam?._id,
  });

  const { handleChangeEcosystem } = useChangeEcosystem();

  const [searchParams, setSearch] = useSearchParams();
  const dispatch = useAppDispatch();

  // -- States
  const [showLocationDrawer, setShowLocationDrawer] = useState(false);
  const [showCalendarDrawer, setShowCalendarDrawer] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<ILocation[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);

  // -- Hooks
  useEffect(() => {
    if (activities.length == 0) return;

    const isFromHome =
      activityDefinition?.locationOption === LocationOptionsEnum.FromHome;

    const availableLocations: ILocation[] = [];
    const blockedDates: BlockedDate[] = [];
    const availableDates: Date[] = [];

    for (const activity of activities) {
      const activityLocation = getLocationFromAddress(activity.address);
      const activityDate = new Date(activity.startDate);

      const isTeamEvent =
        activityDefinition?.eventApplicationType === EEventApplicationType.Team;

      // Activities with application are blocked
      if (activity.userApplication && !isTeamEvent) {
        blockedDates.push({ date: activityDate, message: 'Already booked' });
      } else {
        const dueDate = activity?.dueDate && new Date(activity?.dueDate);
        const endDate = activity?.endDate && new Date(activity?.endDate);
        if (isDateBlocked(dueDate) || isDateBlocked(endDate)) {
          blockedDates.push({ date: activityDate, message: 'Closed' });
        }
      }

      // Ensure only dates related to selected location
      if (
        isFromHome ||
        (filters.location &&
          isLocationEqual(filters.location, activityLocation))
      ) {
        availableDates.push(activityDate);
      }

      // Prevent current selected location from list
      const locationIsSelected =
        filters?.location &&
        isLocationEqual(activityLocation, filters!.location);
      if (locationIsSelected) continue;

      // Ensure only unique locations will be shown
      const sameLocation = availableLocations.some((someLocation) =>
        isLocationEqual(someLocation, activityLocation),
      );
      if (!sameLocation) availableLocations.push(activityLocation);
    }

    setAvailableDates(availableDates);
    setAvailableLocations(availableLocations);
    setBlockedDates(blockedDates);
  }, [activityDefinition, activities, filters]);

  useEffect(() => {
    if (teamInvite) {
      const selectedActivity = activities.find(
        (a) => a._id === teamInvite.activity,
      );
      setSelectedActivity(selectedActivity);
    }
  }, [activities, teamInvite, setSelectedActivity]);

  const openModal = () => {
    modalRef.current?.open();
  };

  const closeModal = () => {
    modalRef.current?.close();
  };

  // -- Handlers
  const handleApplication = async () => {
    if (!teamInvite) return;

    if (!isAuthenticated) {
      toast.info(
        'You need to sign in or sign up to accept the team  invitation!',
      );
      dispatch(
        redirectSliceActions.addTeamInviteRedirect({
          hasInvite: true,
          code: teamInvite.code,
          inviteURL: teamInvite.inviteURL,
          activityId: selectedActivity?._id,
          activityDefinitionId: selectedActivity?.activityDefinition,
        }),
      );
      goToRoute(PAGES.Root);
      return;
    }

    try {
      if (!user?._id) return;
      setIsSubmitting(true);
      await TeamService.acceptInvitation(teamInvite.code);
      const ecosystemId =
        teamInvite?.inviteMembers?.configurations[0]?.ecosystemId;

      if (ecosystemId) {
        await handleChangeEcosystem(ecosystemId);
      }

      queryClient.invalidateQueries();
      openModal();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      toast.error(message || 'Error applying to activity');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewEvent = () => {
    closeModal();
    goToRoute(
      PAGES.ActivitiesDetails.replace(
        ':id',
        selectedActivity?.activityDefinition || '',
      ),
    );
  };

  const handleNewLocation = (newLocation: ILocation | null) => {
    setFilters({ location: newLocation, startDate: null });
    if (!newLocation) return;
    resolveActivity({ location: newLocation });
  };

  const handleNewDate = (newDate: Date) => {
    closeMobileCalendarDrawer();
    setFilters({ location: filters.location, startDate: newDate });
    resolveActivity({
      startDate: newDate,
      location: filters.location || undefined,
    });
  };

  const resolveActivity = (params: {
    startDate?: Date;
    location?: ILocation;
  }) => {
    const { location, startDate } = params;
    if (!location && !startDate) return;

    for (const activity of activities) {
      const activityLocation = getLocationFromAddress(activity.address);
      const activityDate = new Date(activity.startDate);

      const locationMatches =
        location && isLocationEqual(location, activityLocation);
      const dateMatches = startDate && isDateEqual(startDate, activityDate);

      if (
        (!startDate && locationMatches) ||
        (!location && dateMatches) ||
        (locationMatches && dateMatches)
      ) {
        const params = new URLSearchParams(searchParams);
        params.set('selected', activity._id);
        setSearch(params.toString());
        break;
      }
    }
  };

  const handleCreateNewTeam = () => {
    if (platform === ExecutionPlatform.WEBVIEW) {
      goToRoute(PAGES.CreateTeam);
      return;
    }
    window.open(PAGES.CreateTeam);
  };

  const handleFindOpportunities = () => {
    goToRoute(PAGES.Opportunities);
  };

  const openMobileCalendarDrawer = () => {
    setShowCalendarDrawer(true);
  };

  const closeMobileCalendarDrawer = () => {
    setShowCalendarDrawer(false);
  };

  const openMobileLocationDrawer = () => {
    setShowLocationDrawer(true);
  };

  const closeMobileLocationDrawer = () => {
    setShowLocationDrawer(false);
  };

  // Computed
  const styleMap = activityType
    ? ApplicationActionStyleMapByType.get(activityType)
    : undefined;

  const spotsAvailable = getActivitySpotsAvailable(selectedActivity);

  // --Render statements
  const isFromHome =
    activityDefinition?.locationOption === LocationOptionsEnum.FromHome;

  return {
    ...teamController,
    spotsAvailable,
    isFromHome,
    availableLocations,
    availableDates,
    blockedDates,
    handleNewDate,
    handleNewLocation,
    handleApplication,
    applicationAvailability,
    styleMap,
    activityType,
    filters,
    selectedActivity,
    showCalendarDrawer,
    openMobileCalendarDrawer,
    closeMobileCalendarDrawer,
    showLocationDrawer,
    openMobileLocationDrawer,
    closeMobileLocationDrawer,
    title,
    headerStyle,
    handleCreateNewTeam,
    isSubmitting,
    closeModal,
    modalRef,
    handleFindOpportunities,
    handleViewEvent,
    isAuthenticated,
  };
};
